import { Row } from 'react-table';
import styles from './PortDetails.module.scss';
import { PortTerminalData, PortTerminalsOrderParams, PortTerminalsRequest } from 'ais-facade-api/api';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useGetTerminalCatalogMutation } from 'app/api/facade';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { DropDownItem, Dropdown, Icon, Pagination, Table, Typo } from 'app/components';
import { t } from 'i18next';

import { setFindTerminal } from 'app/utils/selectedTargetsSlice';
import { throttle } from 'app/helpers';
import clsx from 'clsx';

const TerminalActionCell = ({ terminal }: { terminal: PortTerminalData }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [dropdown, setDropdown] = useState(false);
  const canBeShown = Boolean(terminal.lat && terminal.lon);

  function showVessel() {
    dispatch(setFindTerminal(terminal));
    navigate('/mapview');
  }
  return (
    <>
      <button
        className={styles.icon_button}
        aria-expanded={dropdown ? 'true' : 'false'}
        onClick={() => setDropdown((prev) => !prev)}
      >
        <Icon name="more-icon" color="var(--default-primary)" width="13px" height="3px" />
      </button>
      <Dropdown
        dropdown={dropdown}
        onClickOutside={() => {
          setDropdown(false);
        }}
      >
        <DropDownItem>
          <button
            className={clsx(styles.button, { [styles.disabled]: !canBeShown })}
            aria-disabled={!canBeShown}
            disabled={!canBeShown}
            onClick={canBeShown ? showVessel : undefined}
          >
            {t('ShowOnMap')}
          </button>
        </DropDownItem>
      </Dropdown>
    </>
  );
};
const terminalColumns = [
  {
    Header: 'Terminal',
    accessor: 'code',
    cellClassName: `${styles.link}`,
    Cell: ({ row: { original: terminal } }: { row: Row<PortTerminalData> }) =>
      typeof terminal.code === 'string' ? terminal.code : <>&mdash;</>,
    canSort: true,
    sortBy: 'CODE',
  },
  {
    Header: 'Name',
    accessor: 'staticData?.name',
    Cell: ({ row: { original: terminal } }: { row: Row<PortTerminalData> }) => (
      <>{typeof terminal.name === 'string' ? terminal.name : <>&mdash;</>}</>
    ),
    canSort: true,
    sortBy: 'NAME',
  },
  {
    Header: <Icon name="more-icon" width="13px" height="3px" />,
    accessor: 'actionCell',
    width: 34,
    minWidth: 34,
    maxWidth: 34,
    disableSortBy: true,
    Cell: ({ row: { original: terminal } }: { row: Row<PortTerminalData> }) => (
      <div className={clsx(styles.center_icon_cell, styles.action_cell)}>
        <TerminalActionCell terminal={terminal} />
      </div>
    ),
  },
];

export interface TerminalsSortingInterface {
  direction: PortTerminalsOrderParams['direction'] | 'default';
  by: PortTerminalsOrderParams['by'];
}

const initialSort: PortTerminalsOrderParams = {
  direction: 'ASC',
  by: 'CODE',
};

export const PortTerminals = () => {
  const { portKey } = useParams();
  const user = useAppSelector((state) => state.userSlice.user);
  const [getTerminals, { data: terminals, ...terminalsRequest }] = useGetTerminalCatalogMutation();
  const debounceGetTerminalsHandler = useCallback(throttle(getTerminals, 500), []);

  const [sorting, setSorting] = useState<TerminalsSortingInterface>(initialSort);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);

  const initialBody: PortTerminalsRequest = useMemo(
    () => ({
      order: initialSort,
      pagination: {
        offset: 0,
        limit: 20,
      },
    }),
    [initialSort],
  );
  useEffect(() => {
    terminalsRequest.reset();
  }, []);
  const columns = useMemo(() => terminalColumns, []);

  useEffect(() => {
    if (user && portKey) {
      setCurrentPage(1);
      debounceGetTerminalsHandler({
        key: portKey,
        body: {
          ...initialBody,
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      });
    }
  }, [sorting]);

  useEffect(() => {
    if (user && portKey) {
      debounceGetTerminalsHandler({
        key: portKey,
        body: {
          ...initialBody,
          pagination: {
            offset: currentOffset,
            limit: 20,
          },
          order:
            sorting.direction === 'default'
              ? initialSort
              : {
                direction: sorting.direction,
                by: sorting.by,
              },
        },
      });
    }
  }, [currentOffset]);

  return (
    <>
      <div className={styles.table_header}>
        <Typo value={`${t('Terminals')}: ${typeof terminals?.total === 'number' ? `${terminals.total}` : '–'}`} size="m" />
      </div>
      {terminalsRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}

      {!terminalsRequest.isLoading && terminals?.results?.length === 0 && <p className={styles.no_data}>{t('NoResultsFound')}</p>}

      {terminals?.results && terminals.results.length > 0 && terminals.total ? (
        <div className={styles.ship_calls}>
          <div className={styles.table_container}>
            <Table manualSort={true} sorting={sorting} columns={columns} data={terminals.results} setSorting={setSorting} />
          </div>
          <div className={styles.pagination_container}>
            <Pagination
              totalCount={terminals.total}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};
